import {
  getQuantityHandledLogistician,
  getQuantityHandledMerchant,
  getQuantityToHandleLogistician,
  getQuantityToHandleMerchant,
} from "@/utils/return-requests";
import type {
  ReturnRequestDto,
  ReturnRequestLineItemDto,
} from "@babackapp/private-api-client/types";
import { getLineItemQuantityAlreadyValidatedByMerchant } from "../../utils/get-line-item-quantity-already-validated-by-merchant";
import { getLineItemReadyToBeTreatedByLogisticianQuantity } from "../../utils/get-line-item-ready-to-be-treated-by-logistician-quantity";
import { getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithWorkflowLogistician } from "../../utils/get-line-item-ready-to-be-treated-by-merchant-selectable-quantity-with-workflow-logistician";
import { getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithoutWorkflowLogistician } from "../../utils/get-line-item-ready-to-be-treated-by-merchant-selectable-quantity-without-workflow-logistician";
import { isLineItemThatCanBeHandledByTheMerchantWithWorkflowLogistician } from "@babackapp/calculations/is-line-item-that-can-be-handled-by-merchant-with-workflow-logistician";
import { isLineItemThatCanBeHandledByTheMerchantWithoutWorkflowLogistician } from "@babackapp/calculations/is-line-item-that-can-be-handled-by-the-merchant-without-workflow-logistician";

const isLineItemToShowToHandleMerchant = (
  lineItem: ReturnRequestLineItemDto,
  hasWorklowLogisticianActivated: boolean,
) =>
  lineItem.quantity > 0 &&
  getQuantityToHandleMerchant(lineItem, {
    notValidatedByLogistician: !hasWorklowLogisticianActivated,
  }) > 0;

const isLineItemToShowToHandleLogistician = (
  lineItem: ReturnRequestLineItemDto,
) => lineItem.quantity > 0 && getQuantityToHandleLogistician(lineItem) > 0;

const isLineItemHandledOk = (
  lineItem: ReturnRequestLineItemDto,
  hasAdminRight: boolean,
) =>
  lineItem.quantity > 0 &&
  (hasAdminRight
    ? getQuantityHandledMerchant(lineItem, { status: "ok" }) > 0
    : getQuantityHandledLogistician(lineItem, { status: "ok" }) > 0);

const isLineItemHandledRejected = (
  lineItem: ReturnRequestLineItemDto,
  isLogistician: boolean,
  hasAdminRight: boolean,
) => {
  if (isLogistician) {
    return (
      lineItem.quantity > 0 &&
      (getQuantityHandledMerchant(lineItem, { status: "rejected" }) > 0 ||
        getQuantityHandledLogistician(lineItem, { status: "rejected" }) > 0)
    );
  }
  if (hasAdminRight) {
    return (
      lineItem.quantity > 0 &&
      (getQuantityHandledMerchant(lineItem, { status: "rejected" }) > 0 ||
        getQuantityHandledLogistician(lineItem, { status: "rejected" }) -
          getQuantityHandledMerchant(lineItem, { status: "ok" }) >
          0)
    );
  }
};

const isLineItemNotToHandleMerchant = (lineItem: ReturnRequestLineItemDto) =>
  lineItem.quantity === 0 || lineItem.quantity_bought > lineItem.quantity;

const isLineItemNotTreatedByLogistician = (
  lineItem: ReturnRequestLineItemDto,
) => {
  const quantityReadyToBeTreatedByLogistician =
    getLineItemReadyToBeTreatedByLogisticianQuantity({ lineItem });
  return quantityReadyToBeTreatedByLogistician > 0;
};

const isLineItemValidatedByMerchant = (lineItem: ReturnRequestLineItemDto) => {
  return getLineItemQuantityAlreadyValidatedByMerchant({ lineItem }) > 0;
};

const isLineItemThatCanBeHandledByTheMerchant = (
  lineItem: ReturnRequestLineItemDto,
  { workflowLogisticianActivated }: { workflowLogisticianActivated: boolean },
) => {
  if (workflowLogisticianActivated) {
    return getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithWorkflowLogistician(
      { lineItem },
    );
  }
  return getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithoutWorkflowLogistician(
    { lineItem },
  );
};
// lineItem.quantity > 0 &&
// getQuantityToHandleMerchant(lineItem, {
//   notValidatedByLogistician: true,
// }) > 0;

export const isLineItemRejected = (lineItem: ReturnRequestLineItemDto) =>
  getQuantityHandledLogistician(lineItem, {
    status: "rejected",
  }) -
    Math.max(
      getQuantityHandledMerchant(lineItem, { status: "ok" }) -
        getQuantityHandledLogistician(lineItem, { status: "ok" }),
      0,
    ) >
  0;

function filterLineItemsToShowToHandle(
  returnRequest: ReturnRequestDto,
  hasWorkflowLogisticianActivated: boolean,
  hasAdminRight: boolean,
) {
  return hasAdminRight
    ? filterLineItemsToShowToHandleMerchant(
        returnRequest,
        hasWorkflowLogisticianActivated,
      )
    : filterLineItemsToShowToHandleLogistician(returnRequest);
}

function filterLineItemsHandledOk(
  returnRequest: ReturnRequestDto,
  hasAdminRight: boolean,
) {
  return returnRequest.line_items.filter((lineItem) =>
    isLineItemHandledOk(lineItem, hasAdminRight),
  );
}

function filterLineItemsHandledRejected(
  returnRequest: ReturnRequestDto,
  isLogistician: boolean,
  hasAdminRight: boolean,
) {
  return returnRequest.line_items.filter((lineItem) =>
    isLineItemHandledRejected(lineItem, isLogistician, hasAdminRight),
  );
}

function filterLineItemsNotToHandleMerchant(returnRequest: ReturnRequestDto) {
  return returnRequest.line_items.filter(isLineItemNotToHandleMerchant);
}

function filterLineItemsToShowToHandleMerchant(
  returnRequest: ReturnRequestDto,
  hasWorklowLogisticianActivated: boolean,
) {
  return returnRequest.line_items.filter((lineItem) =>
    isLineItemToShowToHandleMerchant(lineItem, hasWorklowLogisticianActivated),
  );
}

export function filterLineItemsThatCanBeHandledByTheMerchant(
  returnRequest: ReturnRequestDto,
  workflowLogistician: boolean,
) {
  return returnRequest.line_items.filter(
    workflowLogistician
      ? isLineItemThatCanBeHandledByTheMerchantWithWorkflowLogistician
      : isLineItemThatCanBeHandledByTheMerchantWithoutWorkflowLogistician,
  );
}

export function filterLineItemsToShowToHandleLogistician(
  returnRequest: ReturnRequestDto,
) {
  return returnRequest.line_items.filter(isLineItemToShowToHandleLogistician);
}

function filterLineItemsRejected(returnRequest: ReturnRequestDto) {
  return returnRequest.line_items.filter(isLineItemRejected);
}
